import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, Field, EmptyState, inputCls, splitName, RefBadge, withRef, SortableTh } from "@/components/shared";
import PhoneField from "@/components/PhoneField";
import Timeline from "@/components/Timeline";

const DOC_KEYS = ["id", "acte_de_propriete", "diagnostics", "dpe", "erp", "mandate", "photos", "floorplan"];
const DOC_LABELS = {
  id: "ID", acte_de_propriete: "Acte de propriété", diagnostics: "Diagnostics",
  dpe: "DPE", erp: "ERP", mandate: "Mandate / Mandat", photos: "Photos", floorplan: "Floorplan / Plan",
};
const STATUSES = ["prospect", "valuation_booked", "valuation_completed", "mandate_pending", "live", "under_offer", "sold"];

const emptySeller = {
  name: "", first_name: "", last_name: "", email: "", phone: "",
  date_of_birth: "", place_of_birth: "", country_of_birth: "", nationality: "",
  address: "", preferred_contact_method: "",
  property_id: "", property_name: "", status: "prospect",
  documents: Object.fromEntries(DOC_KEYS.map((k) => [k, false])),
  last_contact: "", next_action: "", next_action_date: "", notes: "",
};

export default function Sellers() {
  const { items, search, setSearch, reload } = useSearchableList("/sellers");
  const [properties, setProperties] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const { t, lang } = useLang();

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const statusLabel = {
    prospect: t("prospect"), valuation_booked: t("valuationBooked"), valuation_completed: t("valuationCompleted"),
    mandate_pending: t("mandatePending"), live: t("live"), under_offer: t("underOffer"), sold: t("sold"),
  };
  const statusColor = {
    prospect: "bg-stone-100 text-stone-600", valuation_booked: "bg-stone-100 text-stone-600",
    valuation_completed: "bg-[#F5F5F4] text-[#57534E]", mandate_pending: "bg-orange-50 text-[#9A3412]",
    live: "bg-slate-100 text-slate-800", under_offer: "bg-green-50 text-[#3F6212]", sold: "bg-stone-800 text-white",
  };

  useEffect(() => {
    api.get("/properties").then((r) => setProperties(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    const fullName = `${form.first_name || ""} ${form.last_name || ""}`.trim();
    if (!fullName) return toast.error(t("required"));
    setSaving(true);
    const payload = { ...form, name: fullName };
    try {
      if (form.id) await api.put(`/sellers/${form.id}`, payload);
      else await api.post("/sellers", payload);
      toast.success(t("saved"));
      setForm(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/sellers/${form.id}`);
    toast.success(t("deleted"));
    setForm(null);
    reload();
  };

  return (
    <div data-testid="sellers-page">
      <PageHeader
        title={t("sellers")}
        action={
          <button data-testid="add-seller-button" onClick={() => setForm(JSON.parse(JSON.stringify(emptySeller)))}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("addSeller")}
          </button>
        }
      />
      <div className="relative mb-6 max-w-md">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="sellers-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")} className={`${inputCls} pl-9`} />
      </div>

      {items.length === 0 ? <EmptyState message={t("noResults")} /> : (
        <SellersTable
          items={items}
          properties={properties}
          statusLabel={statusLabel}
          statusColor={statusColor}
          onOpen={(s) => setForm({ ...emptySeller, ...s, ...splitName(s), documents: { ...emptySeller.documents, ...(s.documents || {}) } })}
          sortKey={sortKey}
          sortDir={sortDir}
          toggleSort={toggleSort}
          t={t}
          lang={lang}
        />
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl bg-white">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900 flex items-center gap-3 flex-wrap">
                  <span>{form.id ? t("editSeller") : t("addSeller")}</span>
                  <RefBadge code={form.ref_code} size="md" />
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <Field label={t("firstName")}><input data-testid="seller-firstname-input" className={inputCls} value={form.first_name} onChange={set("first_name")} /></Field>
                <Field label={t("surname")}><input data-testid="seller-surname-input" className={inputCls} value={form.last_name} onChange={set("last_name")} /></Field>
                <Field label={t("status")}>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="seller-status-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{statusLabel[s]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("email")}><input data-testid="seller-email-input" className={inputCls} value={form.email} onChange={set("email")} /></Field>
                <Field label={t("phone")}><PhoneField testId="seller-phone-input" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></Field>
                <Field label={t("dateOfBirth")}><input data-testid="seller-dob-input" type="date" className={inputCls} value={form.date_of_birth || ""} onChange={set("date_of_birth")} /></Field>
                <Field label={t("nationality")}><input data-testid="seller-nationality-input" className={inputCls} value={form.nationality || ""} onChange={set("nationality")} /></Field>
                <Field label={t("placeOfBirth")}><input data-testid="seller-pob-input" className={inputCls} value={form.place_of_birth || ""} onChange={set("place_of_birth")} /></Field>
                <Field label={t("countryOfBirth")}><input data-testid="seller-cob-input" className={inputCls} value={form.country_of_birth || ""} onChange={set("country_of_birth")} /></Field>
                <Field label={t("address")} className="sm:col-span-2"><textarea rows={2} data-testid="seller-address-input" className={inputCls} value={form.address || ""} onChange={set("address")} /></Field>
                <Field label={t("preferredContact")}>
                  <Select value={form.preferred_contact_method || "none"} onValueChange={(v) => setForm({ ...form, preferred_contact_method: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="seller-contact-method-select" className={inputCls}><SelectValue placeholder={t("none")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("none")}</SelectItem>
                      <SelectItem value="email">{t("email")}</SelectItem>
                      <SelectItem value="phone">{t("phone")}</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("linkedProperty")} className="sm:col-span-2">
                  <Select value={form.property_id || "none"}
                    onValueChange={(v) => {
                      const p = properties.find((x) => x.id === v);
                      setForm({ ...form, property_id: v === "none" ? "" : v, property_name: p ? p.name : "" });
                    }}>
                    <SelectTrigger data-testid="seller-property-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t("none")}</SelectItem>
                      {properties.map((p) => <SelectItem key={p.id} value={p.id}>{withRef(p.ref_code, p.name)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("documentsChecklist")} className="sm:col-span-2">
                  <div className="grid grid-cols-2 gap-2.5 bg-stone-50 border border-stone-200 rounded-lg p-4">
                    {DOC_KEYS.map((k) => (
                      <label key={k} className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
                        <Checkbox
                          data-testid={`seller-doc-${k}-checkbox`}
                          checked={!!form.documents[k]}
                          onCheckedChange={(c) => setForm({ ...form, documents: { ...form.documents, [k]: !!c } })}
                        />
                        {DOC_LABELS[k]}
                      </label>
                    ))}
                  </div>
                  <p className="text-[11px] text-stone-400 mt-2">
                    Tick to confirm you have received each document. For GDPR compliance, keep sensitive ID files in your own secure storage — not here.
                  </p>
                </Field>
                <Field label={t("lastContact")}><input data-testid="seller-lastcontact-input" type="date" className={inputCls} value={form.last_contact} onChange={set("last_contact")} /></Field>
                <Field label={t("nextActionDate")}><input data-testid="seller-nextaction-date-input" type="date" className={inputCls} value={form.next_action_date || ""} onChange={set("next_action_date")} /></Field>
                <Field label={t("nextAction")} className="sm:col-span-2">
                  <input data-testid="seller-nextaction-input" className={inputCls} value={form.next_action} onChange={set("next_action")} />
                  <p className="text-[11px] text-stone-400 mt-1">{t("autoTaskHint")}</p>
                </Field>
                <Field label={t("notes")} className="sm:col-span-2"><textarea data-testid="seller-notes-input" rows={3} className={inputCls} value={form.notes} onChange={set("notes")} /></Field>
              </div>
              {form.id && (
                <div className="mt-8 pt-6 border-t border-stone-200">
                  <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">{t("timelineTab")}</h3>
                  <Timeline linkKey="seller_id" linkId={form.id} linkName={form.name} />
                </div>
              )}
              <div className="flex items-center justify-between mt-6">
                {form.id ? (
                  <button data-testid="delete-seller-button" onClick={remove} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                    <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                  </button>
                ) : <span />}
                <div className="flex gap-3">
                  <button data-testid="cancel-seller-button" onClick={() => setForm(null)} className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
                  <button data-testid="save-seller-button" onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SellersTable({ items, properties, statusLabel, statusColor, onOpen, sortKey, sortDir, toggleSort, t, lang }) {
  const sorted = useMemo(() => {
    const list = [...items];
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (s) => {
      if (sortKey === "missing") return DOC_KEYS.filter((k) => !(s.documents || {})[k]).length;
      if (sortKey === "last_contact") return s.last_contact || "";
      if (sortKey === "next_action_date") return s.next_action_date || "";
      return (s[sortKey] || "").toString().toLowerCase();
    };
    list.sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : 0));
    return list;
  }, [items, sortKey, sortDir]);
  return (
    <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="sellers-table-wrap">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              <SortableTh onClick={() => toggleSort("ref_code")} active={sortKey === "ref_code"} dir={sortDir}>{t("ref")}</SortableTh>
              <SortableTh onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>{t("name")}</SortableTh>
              <SortableTh onClick={() => toggleSort("status")} active={sortKey === "status"} dir={sortDir}>{t("status")}</SortableTh>
              <SortableTh onClick={() => toggleSort("property_name")} active={sortKey === "property_name"} dir={sortDir}>{t("property")}</SortableTh>
              <SortableTh onClick={() => toggleSort("missing")} active={sortKey === "missing"} dir={sortDir} align="right">{t("missingDocuments")}</SortableTh>
              <SortableTh onClick={() => toggleSort("last_contact")} active={sortKey === "last_contact"} dir={sortDir}>{t("lastContact")}</SortableTh>
              <SortableTh onClick={() => toggleSort("next_action_date")} active={sortKey === "next_action_date"} dir={sortDir}>{t("nextAction")}</SortableTh>
              <th className="py-2.5 px-3 w-10"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {sorted.map((s) => {
              const missing = DOC_KEYS.filter((k) => !(s.documents || {})[k]).length;
              const propertyLabel = s.property_name
                ? withRef(s.property_ref_code || (properties.find((p) => p.id === s.property_id) || {}).ref_code, s.property_name)
                : "—";
              const lastContactStale = s.last_contact && ((Date.now() - new Date(s.last_contact).getTime()) / (86400000) > 30);
              return (
                <tr
                  key={s.id}
                  data-testid={`seller-row-${s.id}`}
                  onClick={() => onOpen(s)}
                  className="hover:bg-stone-50 cursor-pointer transition-colors"
                >
                  <td className="py-2.5 px-3"><RefBadge code={s.ref_code} /></td>
                  <td className="py-2.5 px-3 font-medium text-stone-900">{s.name}</td>
                  <td className="py-2.5 px-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap ${statusColor[s.status] || "bg-stone-100 text-stone-600"}`}>
                      {statusLabel[s.status] || s.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-stone-600 max-w-[220px] truncate">{propertyLabel}</td>
                  <td className="py-2.5 px-3 text-right">
                    {missing > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-[#9A3412] px-2 py-0.5 text-[11px] font-medium">
                        {missing}
                      </span>
                    ) : (
                      <span className="text-stone-300">—</span>
                    )}
                  </td>
                  <td className={`py-2.5 px-3 whitespace-nowrap ${lastContactStale ? "text-[#9A3412]" : "text-stone-600"}`}>
                    {s.last_contact ? fmtDate(s.last_contact, lang) : "—"}
                  </td>
                  <td className="py-2.5 px-3 text-stone-600 max-w-[220px]">
                    {s.next_action ? (
                      <div className="truncate">
                        <span className="text-[#9A3412]">→ {s.next_action}</span>
                        {s.next_action_date && <span className="text-stone-400 ml-1">({fmtDate(s.next_action_date, lang)})</span>}
                      </div>
                    ) : "—"}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <Pencil size={14} strokeWidth={1.5} className="text-stone-300 inline-block" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
